import { toDisplay, fromDisplay } from '../../lib/currency';

/**
 * «خدمات» — the edit buffer for what one practitioner provides here, and on what
 * terms (docs/CLINIC_PRACTITIONER_ENGAGEMENT_DESIGN.md §۴). Since the standalone
 * doctor↔clinic contract was removed, this is where ALL of a practitioner's money
 * at a clinic is edited: their share of each service, and their visit price.
 *
 * Pure, so the drawer and the phone sheet derive dirty / invalid / the input
 * from one implementation.
 *
 * A row is `{ declarationId, isDefault, mode, value, basis, priceToman }`:
 *   mode ''        no personal rule — the service's general rule applies, and
 *                  with none at all the practitioner's share is 0%
 *   mode percent   value is the PRACTITIONER's percentage, as typed
 *   mode fixed     value is TOMAN, as typed; rials exist only in the input
 *   priceToman     a VISIT only: their own visit price, as typed. '' = the
 *                  clinic's visit price.
 *
 * Only the practitioner's share is stored; the clinic's is derived for display.
 */

export const MODE = { GENERAL: '', PERCENT: 'percent', FIXED: 'fixed' };
export const BASES = ['gross', 'net_patient', 'collected'];
export const CONSULTATION = 'consultation';

export const PROBLEM = {
  NO_DEFAULT: 'noDefault',
  VALUE_REQUIRED: 'valueRequired',
  PERCENT_RANGE: 'percentRange',
  FIXED_POSITIVE: 'fixedPositive',
  VISIT_PRICE_REQUIRED: 'visitPriceRequired',
};

/** Everything a row needs to know about its service, from the stored services and the offer list. */
export function serviceMeta(data) {
  const m = new Map();
  for (const o of data?.available || []) m.set(o.declarationId, { ...o, pending: true });
  for (const s of data?.services || []) m.set(s.declarationId, s);
  return m;
}

export const isVisitService = (s) => s?.orderType === CONSULTATION;

/**
 * Whether a visit row may leave its price empty: only when it charges the
 * clinic's own visit price and the clinic row actually has one.
 */
const clinicPriceApplies = (s) => !!s && !s.priceIsPersonal && s.isActive !== false && Number(s.unitPrice) > 0;

/**
 * The edit buffer, in DISPLAY units of the clinic's currency (`data.currency`;
 * a service's own wins, and blank is IRR — toman, as before currencies existed).
 */
export function rowsFromServices(data) {
  return (data?.services || []).map((s) => {
    const own = s.commissionIsPersonal ? s.commission : null;
    const currency = s.currency || data?.currency;
    return {
      declarationId: s.declarationId,
      isDefault: !!s.isDefault,
      mode: own ? own.mode : MODE.GENERAL,
      value: !own ? '' : String(own.mode === MODE.FIXED ? toDisplay(own.value, currency) : own.value),
      basis: own?.basis || 'gross',
      priceToman: isVisitService(s) && s.priceIsPersonal ? String(toDisplay(s.unitPrice, currency)) : '',
    };
  });
}

const canonical = (rows) => JSON.stringify(
  rows
    .map((r) => ({
      id: r.declarationId,
      d: !!r.isDefault,
      m: r.mode || '',
      v: r.mode ? String(r.value).trim() : '',
      b: r.mode === MODE.PERCENT ? (r.basis || 'gross') : '',
      p: String(r.priceToman ?? '').trim(),
    }))
    .sort((a, b) => a.id.localeCompare(b.id)),
);

export const rowsDirty = (rows, data) => canonical(rows) !== canonical(rowsFromServices(data));

/** `[{ declarationId | null, problem }]` — empty when the buffer can be saved. */
export function rowProblems(rows, meta = new Map()) {
  const out = [];
  if (rows.length > 0 && !rows.some((r) => r.isDefault)) out.push({ declarationId: null, problem: PROBLEM.NO_DEFAULT });
  for (const r of rows) {
    const s = meta.get(r.declarationId);
    if (isVisitService(s)) {
      const price = String(r.priceToman ?? '').trim();
      if (price === '' ? !clinicPriceApplies(s) : !(Number(price) > 0)) {
        out.push({ declarationId: r.declarationId, problem: PROBLEM.VISIT_PRICE_REQUIRED });
      }
    }
    if (!r.mode) continue;
    const raw = String(r.value ?? '').trim();
    const n = Number(raw);
    if (raw === '' || !Number.isFinite(n)) out.push({ declarationId: r.declarationId, problem: PROBLEM.VALUE_REQUIRED });
    else if (r.mode === MODE.PERCENT && (n < 0 || n > 100)) out.push({ declarationId: r.declarationId, problem: PROBLEM.PERCENT_RANGE });
    else if (r.mode === MODE.FIXED && n <= 0) out.push({ declarationId: r.declarationId, problem: PROBLEM.FIXED_POSITIVE });
  }
  return out;
}

/** The buffer back in MINOR units of `currency` (the clinic's; blank is IRR). */
export function toTermsInput(rows, currency) {
  return rows.map((r) => {
    const price = String(r.priceToman ?? '').trim();
    return {
      declarationId: r.declarationId,
      isDefault: !!r.isDefault,
      ...(r.mode
        ? {
          mode: r.mode,
          value: r.mode === MODE.FIXED ? fromDisplay(r.value, currency) : Number(r.value),
          basis: r.basis || 'gross',
        }
        : {}),
      ...(price !== '' ? { priceRial: fromDisplay(price, currency) } : {}),
    };
  });
}

export const addRow = (rows, declarationId) => (rows.some((r) => r.declarationId === declarationId)
  ? rows
  : [...rows, {
    declarationId, isDefault: !rows.some((r) => r.isDefault), mode: MODE.GENERAL, value: '', basis: 'gross', priceToman: '',
  }]);

export function removeRow(rows, declarationId) {
  const removed = rows.find((r) => r.declarationId === declarationId);
  const next = rows.filter((r) => r.declarationId !== declarationId);
  if (removed?.isDefault && next.length && !next.some((r) => r.isDefault)) {
    return next.map((r, i) => (i === 0 ? { ...r, isDefault: true } : r));
  }
  return next;
}

export const setDefault = (rows, declarationId) => rows.map((r) => ({ ...r, isDefault: r.declarationId === declarationId }));

export const patchRow = (rows, declarationId, patch) => rows.map((r) => (r.declarationId === declarationId ? { ...r, ...patch } : r));

/** The clinic's share, derived. Null when it is not a percentage at all. */
export function clinicSharePct(mode, value) {
  if (mode !== MODE.PERCENT) return null;
  const n = Number(value);
  if (String(value ?? '').trim() === '' || !Number.isFinite(n) || n < 0 || n > 100) return null;
  return Math.round((100 - n) * 100) / 100;
}
